import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormOption } from 'src/app/shared/models/form-option.model';
import { NgIf, NgFor } from '@angular/common';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';

/**
 * Component to help manage multi-select form inputs. It generates a list
 * of checkboxes, with each checkbox associated with an option.
 * Each checkbox that is checked adds its value to the passed FormControl, whic
 * is assumed to be an array.
 *
 * The component is compatible with "Set All WNL" checkboxes,
 * meaning that if the checkbox group is specified to have a "WNL" option, it will
 * enable/disable the other checkboxes if the WNL option is checked.
 *
 * To see examples where this component is used, visit some of the classes that extend the
 * AssessmentFormComponent class (e.g. Neurological, Genitourinary, Musculoskeletal).
 *
 * Note: WNL = "Within Normal Limits"
 */
@Component({
  selector: 'emr-checkbox-group',
  templateUrl: './emr-checkbox-group.component.html',
  styleUrls: ['./emr-checkbox-group.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    CheckboxModule,
    TableModule,
    FormsModule,
    NgFor,
    ReactiveFormsModule,
  ],
})
export class EmrCheckboxGroupComponent implements OnInit, OnChanges {
  /**
   * Controls whether or not to display the "WNL" checkbox.
   */
  @Input() hasWNL = false;

  /**
   * Label that describes to the user what the input is for.
   */
  @Input() label = '';

  /**
   * The value that should automatically fill the form input when "Within Normal Limits" (WNL)
   * is checked.
   */
  @Input() default: any;

  /**
   * The label to display next to the checkbox representing the default option.
   */
  @Input() wnlLabel = '';

  /**
   * The options that will be rendered next to checkboxes.
   */
  @Input() options: FormOption[] = [];

  /**
   * The control for which the checkboxes add or remove values.
   */
  @Input() control: UntypedFormControl = new UntypedFormControl();

  /**
   * Used to toggle whether the form group is within normal limits or not. If true,
   * all of the FormControls within the FormGroup will be set to false.
   */
  @Input() wnl = false;

  /**
   * Emits when the "WNL" checkbox is changed.
   */
  @Output() wnlChange = new EventEmitter<boolean>();

  constructor() { }

  /**
   * Sets the components wnl property to true if the "WNL" checkbox defaults to being checked.
   */
  ngOnInit(): void {
    if (this.default == 'true') {
      this.wnl = true;
    } else {
      this.wnl = false;
    }
  }

  /**
   * Tells the FormControl to change based on whether the WNL property is set
   * or not. This runs each time the "Set All WNL" box is checked, and re-configures
   * the component according to the value of the WNL property.
   */
  ngOnChanges(): void {
    this.toggleWNL();
  }

  /**
   * Toggles the WNL property. Enables/disables input and autofills/clears
   * the default value depending on the value of the WNL checkbox.
   * Emits the new WNL value back to the parent.
   */
  toggleWNL() {
    if (this.wnl) {
      this.control.setValue(this.default, { onlySelf: true });
      this.control.disable({ onlySelf: true });
    } else {
      this.control.enable({ onlySelf: true });
      this.control.setValue([], { onlySelf: true });
    }

    this.wnlChange.emit(this.wnl);
  }
}
